/**
 * Клиент обязательств (E7). Типы согласованы с сервером по ЗНАЧЕНИЯМ (клиент и api типы напрямую
 * не делят) — формы из apps/api/src/obligations/{resolve,service}.ts. Статусы вычисляет сервер
 * (Р-2) и отдаёт готовыми. Путь относительный (`/api/...`): проксирует Caddy/Vite; сессия — cookie
 * HttpOnly (Р-5).
 */

import type { Role } from "./roles";
import type { ApiResult } from "./contracts";
import { formatDate } from "./format";

/** Тип обязательства (Приложение А; значения = enum `ObligationType` сервера). */
export type ObligationType =
  | "supply"
  | "works"
  | "services"
  | "payment_advance"
  | "payment_post"
  | "acceptance"
  | "docs"
  | "guarantee"
  | "security"
  | "ret"
  | "other";

export type ObligationDebtor = "we" | "they";
export type ObligationRuleKind = "fixed" | "relative" | "event";
export type ObligationRuleBase = "contract" | "spec";
export type ObligationDayType = "calendar" | "work" | "bank";
/** Вычисляемый статус (Приложение Б; значения = `ObligationStatus` ядра). */
export type ObligationStatus =
  "awaiting" | "control" | "soon" | "overdue" | "fulfilled" | "dispute" | "cancelled";

/** Наименования типов (Прил. А, термины ТЗ — совпадают с сервером). */
export const OBLIGATION_TYPE_LABELS: Record<ObligationType, string> = {
  supply: "Поставка",
  works: "Работы",
  services: "Услуги",
  payment_advance: "Предоплата",
  payment_post: "Оплата (постоплата)",
  acceptance: "Приёмка / УПД",
  docs: "Передача документов",
  guarantee: "Гарантийные обязательства",
  security: "Обеспечение (БГ / платёж)",
  ret: "Возврат (тара / обеспечение)",
  other: "Иное",
};

/**
 * Ответственная роль ПО УМОЛЧАНИЮ для типа — колонка «Ответственный по умолчанию» Приложения А ТЗ.
 * Нужна форме, чтобы показать, какая роль подставится, если не переопределять (E10). Значения
 * дублируют серверную таблицу (`apps/api/src/obligations/resolve.ts`) — контракт согласован по
 * строкам, как и остальные справочники клиента; источник истины при расхождении — Приложение А.
 * У типа «Иное» умолчания нет: роль назначается вручную.
 */
export const OBLIGATION_DEFAULT_RESP_ROLE: Record<ObligationType, Role | null> = {
  supply: "commercial",
  works: "commercial",
  services: "commercial",
  payment_advance: "accounting",
  payment_post: "accounting",
  acceptance: "commercial",
  docs: "commercial",
  guarantee: "commercial",
  security: "accounting",
  ret: "commercial",
  other: null,
};

/** Денежные типы (Приложение А, колонка «Денежное»): закрываются платежами. */
const MONEY_TYPES: ReadonlySet<ObligationType> = new Set<ObligationType>([
  "payment_advance",
  "payment_post",
  "security",
]);

/**
 * Роли, вносящие события исполнения НЕденежных обязательств (отгрузка, приёмка/УПД, передача
 * документов, «иное»): матрица 4.3 закрепляет их за коммерческим отделом, юристом и админом.
 * Клиентская копия серверного разграничения (`event:delivery:create` / `obligation:fulfill`) —
 * нужна только для подсказки в форме; сам запрет стоит на сервере (правило 5).
 */
const NON_MONEY_EVENT_ROLES: readonly Role[] = ["commercial", "lawyer", "admin"];

/**
 * Сможет ли назначенная роль САМА отметить обязательство исполненным. Назначить ответственным
 * можно любую роль — этого требует адресация уведомлений (ТЗ 4.2.4), но, например, бухгалтерия
 * приёмку/УПД вносить не вправе, и такое обязательство закроют юрист или администратор.
 * У денежных обязательств вопрос не стоит: они закрываются платежами, зона к ним не применяется.
 */
export function canRegisterClosingEvent(type: ObligationType, role: Role): boolean {
  if (MONEY_TYPES.has(type)) return true;
  return NON_MONEY_EVENT_ROLES.includes(role);
}

export const DEBTOR_LABELS: Record<ObligationDebtor, string> = {
  they: "Должен контрагент",
  we: "Должны мы",
};

export const RULE_KIND_LABELS: Record<ObligationRuleKind, string> = {
  fixed: "До конкретной даты",
  relative: "N дней от даты договора/спецификации",
  event: "N дней от исполнения другого обязательства",
};

export const RULE_BASE_LABELS: Record<ObligationRuleBase, string> = {
  contract: "договора",
  spec: "спецификации",
};

export const DAY_TYPE_LABELS: Record<ObligationDayType, string> = {
  calendar: "календарных",
  work: "рабочих",
  bank: "банковских",
};

/** Наименования статусов (Прил. Б) + токены оформления (docs/07; правило 9 — без «сырых» hex). */
export const OBLIGATION_STATUS_META: Record<
  ObligationStatus,
  { readonly label: string; readonly cls: string }
> = {
  awaiting: { label: "Ожидает события", cls: "text-waiting-fg bg-waiting-bg" },
  control: { label: "На контроле", cls: "text-control-fg bg-control-bg" },
  soon: { label: "Срок близко", cls: "text-soon-fg bg-soon-bg" },
  overdue: { label: "Просрочено", cls: "text-overdue-fg bg-overdue-bg" },
  fulfilled: { label: "Исполнено", cls: "text-done-fg bg-done-bg" },
  dispute: { label: "Спор", cls: "text-dispute-fg bg-dispute-bg" },
  cancelled: { label: "Снято", cls: "text-waiting-fg bg-waiting-bg" },
};

/** Обязательство с вычисленным статусом — ответ GET /api/contracts/:id/obligations. */
export interface Obligation {
  readonly id: string;
  readonly contractId: string;
  readonly specificationId: string | null;
  readonly type: ObligationType;
  readonly debtor: ObligationDebtor;
  readonly descr: string;
  readonly amount: string | null;
  /** Назначенная роль (для «иное» — обязательна; NULL — роль выводится из типа). */
  readonly respRole: Role | null;
  readonly ruleKind: ObligationRuleKind;
  readonly ruleDate: string | null;
  readonly ruleBase: ObligationRuleBase | null;
  readonly ruleDays: number | null;
  readonly dayType: ObligationDayType | null;
  readonly triggerObligationId: string | null;
  readonly manualStatus: "dispute" | "cancelled" | null;
  readonly doneDate: string | null;
  /** № судебного дела — только при статусе «Спор» (E9, ТЗ 4.2.5). */
  readonly disputeCaseNo: string | null;
  /** Основание перевода в «Спор» / снятия с контроля (E9). */
  readonly manualStatusReason: string | null;
  /**
   * Дата правового решения «Спор»/«Снято» (D-6). Хранится у обоих статусов; датой СНЯТИЯ
   * обрывается начисление санкций, у «Спора» она информационная (в споре начисление идёт).
   */
  readonly manualStatusDate: string | null;
  /** Срок, от которого юрист велел вести начисление санкций (E9, Р-24). NULL — вычисленный. */
  readonly accrualDueDate: string | null;
  readonly accrualDueReason: string | null;
  // Вычисленные сервером поля (Р-2):
  readonly typeLabel: string;
  readonly money: boolean;
  /** Эффективная ответственная роль (назначенная или дефолт из типа). NULL — «иное» без назначения. */
  readonly responsible: Role | null;
  readonly status: ObligationStatus;
  readonly statusLabel: string;
  readonly due: string | null;
  readonly shifted: boolean;
  readonly awaiting: boolean;
  readonly overdue: number;
  readonly daysLeft: number | null;
  readonly escalated: boolean;
  readonly triggerDescr: string | null;
  /** Покрыто событиями исполнения (E8): платежи — для денежных, отгрузки — для остальных. */
  readonly covered: string;
  /** Остаток = сумма − покрыто. null — сумма обязательства не задана. */
  readonly remaining: string | null;
  /** Сколько событий исполнения зарегистрировано. */
  readonly eventCount: number;
}

/** Поля формы обязательства. Пустая строка в необязательном поле = «нет значения». */
export interface ObligationInput {
  readonly specificationId?: string | null;
  readonly type?: ObligationType;
  readonly debtor?: ObligationDebtor;
  readonly descr?: string;
  readonly amount?: string;
  /**
   * Ответственная роль (E10): роль — переопределение для любого типа, `null` — вернуть умолчание
   * из типа (Прил. А). Поле не передано — назначение остаётся прежним.
   */
  readonly responsible?: Role | null;
  readonly ruleKind?: ObligationRuleKind;
  readonly ruleDate?: string;
  readonly ruleBase?: ObligationRuleBase;
  readonly ruleDays?: number | string;
  readonly dayType?: ObligationDayType;
  readonly triggerObligationId?: string | null;
}

/** Роли ведения обязательств (право `obligation:manage`, ТЗ 4.3): только юрист/админ. */
export const OBLIGATION_MANAGE_ROLES: readonly Role[] = ["lawyer", "admin"];
export function canManageObligations(roles: readonly Role[]): boolean {
  return roles.some((r) => OBLIGATION_MANAGE_ROLES.includes(r));
}

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  const message = body?.message;
  if (res.status === 403)
    return { ok: false, reason: "forbidden", message: message ?? "Недостаточно прав." };
  if (res.status === 409)
    return { ok: false, reason: "conflict", message: message ?? "Конфликт данных." };
  if (res.status === 404)
    return { ok: false, reason: "network", message: message ?? "Обязательство не найдено." };
  if (res.status === 400)
    return { ok: false, reason: "validation", message: message ?? "Проверьте введённые данные." };
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

/** Реестр обязательств договора (GET /api/contracts/:id/obligations). */
export async function fetchObligations(contractId: string): Promise<ApiResult<Obligation[]>> {
  let res: Response;
  try {
    res = await fetch(`/api/contracts/${encodeURIComponent(contractId)}/obligations`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { obligations?: Obligation[] } | null;
  return { ok: true, value: body?.obligations ?? [] };
}

async function sendObligation(
  url: string,
  method: "POST" | "PATCH",
  input: ObligationInput,
): Promise<ApiResult<Obligation>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify(input),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { obligation?: Obligation } | null;
  if (!body?.obligation)
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  return { ok: true, value: body.obligation };
}

/** Добавить обязательство (POST /api/contracts/:id/obligations). */
export function createObligation(
  contractId: string,
  input: ObligationInput,
): Promise<ApiResult<Obligation>> {
  return sendObligation(
    `/api/contracts/${encodeURIComponent(contractId)}/obligations`,
    "POST",
    input,
  );
}

/** Править обязательство (PATCH /api/obligations/:id). */
export function updateObligation(
  id: string,
  input: ObligationInput,
): Promise<ApiResult<Obligation>> {
  return sendObligation(`/api/obligations/${encodeURIComponent(id)}`, "PATCH", input);
}

/** Удалить обязательство (DELETE /api/obligations/:id). */
export async function removeObligation(id: string): Promise<ApiResult<null>> {
  let res: Response;
  try {
    res = await fetch(`/api/obligations/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (res.status === 204) return { ok: true, value: null };
  return toFailure(res);
}

/** Человекочитаемое правило срока (для реестра). Совпадает по смыслу с прототипом ruleText. */
export function describeRule(o: Obligation): string {
  if (o.ruleKind === "fixed") return o.ruleDate ? `до ${formatDate(o.ruleDate)}` : "до даты";
  const days = o.ruleDays ?? "?";
  const dt = o.dayType ? DAY_TYPE_LABELS[o.dayType] : "";
  if (o.ruleKind === "relative") {
    const base = o.ruleBase ? RULE_BASE_LABELS[o.ruleBase] : "договора";
    return `${days} ${dt} дн. с даты ${base}`;
  }
  return `${days} ${dt} дн. с события: ${o.triggerDescr ?? "исполнение обязательства"}`;
}
